/**
 * AgentSelectorDropdown Component
 *
 * A dropdown in the widget header that shows all available brains
 * and allows switching between them. If only one agent, shows it without dropdown.
 * Includes mini image and description for the selected agent.
 */

import React, { useEffect, useRef, useState } from 'react';

import { useEmbedTheme } from '../theme';

// Types
export interface AgentOption {
  type: 'brain';
  id: string;
  name: string;
  displayImage?: string;
  description?: string;
}

interface AgentSelectorDropdownProps {
  agents: AgentOption[];
  selectedAgent: AgentOption | null;
  onSelect: (agent: AgentOption) => void;
  mediaBaseUrl?: string;
}

// Brain icon SVG (used as fallback when no image)
const BrainIconFallback = ({ size = 24, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24A2.5 2.5 0 0 1 9.5 2" />
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24A2.5 2.5 0 0 0 14.5 2" />
  </svg>
);

// Chevron icon
const ChevronDownIcon = ({ rotated = false }: { rotated?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: 'transform 0.2s ease',
      transform: rotated ? 'rotate(180deg)' : 'rotate(0deg)',
    }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const AgentSelectorDropdown: React.FC<AgentSelectorDropdownProps> = ({
  agents,
  selectedAgent,
  onSelect,
  mediaBaseUrl = '',
}) => {
  const { theme, primaryColor } = useEmbedTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper to get image URL
  const getImageUrl = (image?: string) => {
    if (!image) return '';
    if (image.startsWith('http')) return image;
    return mediaBaseUrl ? `${mediaBaseUrl}${image}` : image;
  };

  // Close dropdown when clicking outside
  // Use composedPath() to handle Shadow DOM correctly
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current) return;

      // Use composedPath() to get the actual click path through Shadow DOM
      const path = event.composedPath();
      const clickedInside = path.includes(dropdownRef.current);

      if (!clickedInside) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // If no agents or no selected agent, don't render
  if (agents.length === 0 || !selectedAgent) {
    return null;
  }

  const hasMultipleAgents = agents.length > 1;
  const selectedImageUrl = getImageUrl(selectedAgent.displayImage);

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'relative',
        backgroundColor: theme.colors.background.surface,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadow.sm,
        padding: '8px 10px',
        border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
      }}
    >
      {/* Main Container with Image, Dropdown, and Description */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {/* Mini Image / Fallback Icon */}
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: theme.borderRadius.md,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: theme.colors.background.elevated,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
          }}
        >
          {selectedImageUrl ? (
            <img
              src={selectedImageUrl}
              alt={selectedAgent.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <BrainIconFallback size={20} color={theme.colors.text.tertiary} />
          )}
        </div>

        {/* Right Side: Dropdown + Description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Dropdown Button */}
          <button
            onClick={() => hasMultipleAgents && setIsOpen(!isOpen)}
            disabled={!hasMultipleAgents}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              backgroundColor: theme.colors.background.elevated,
              border: 'none',
              borderRadius: theme.borderRadius.md,
              cursor: hasMultipleAgents ? 'pointer' : 'default',
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              width: 'auto',
              maxWidth: '100%',
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedAgent.name}
            </span>
            {hasMultipleAgents && <ChevronDownIcon rotated={isOpen} />}
          </button>

          {/* Description */}
          {selectedAgent.description && (
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.text.tertiary,
                lineHeight: '1.4',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {selectedAgent.description}
            </p>
          )}
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && hasMultipleAgents && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '46px', // Offset to align with dropdown button (image width + gap)
            marginTop: '4px',
            backgroundColor: theme.colors.background.surface,
            border: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
            borderRadius: theme.borderRadius.md,
            boxShadow: theme.shadow.lg,
            zIndex: 100,
            maxHeight: '240px',
            overflowY: 'auto',
            minWidth: '200px',
          }}
        >
          {agents.map((agent) => {
            const isSelected = selectedAgent.id === agent.id;
            const agentImageUrl = getImageUrl(agent.displayImage);

            return (
              <div
                key={agent.id}
                onClick={() => {
                  onSelect(agent);
                  setIsOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? theme.colors.background.hover : 'transparent',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.background.hover;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {/* Agent Mini Image */}
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: theme.borderRadius.sm,
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: theme.colors.background.elevated,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {agentImageUrl ? (
                    <img
                      src={agentImageUrl}
                      alt={agent.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <BrainIconFallback size={16} color={theme.colors.text.tertiary} />
                  )}
                </div>

                {/* Agent Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      color: theme.colors.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {agent.name}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      color: '#8b5cf6',
                      opacity: 0.9,
                      marginTop: '1px',
                    }}
                  >
                    Brain
                  </div>
                </div>

                {/* Checkmark */}
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={primaryColor}
                    strokeWidth="3"
                    style={{ flexShrink: 0 }}
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AgentSelectorDropdown;
